import SignaturePad from '@/components/admin/SignaturePad';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const FILING_STATUSES = [
  { value: 'single_or_mfs', label: 'Single or Married filing separately' },
  { value: 'married_jointly', label: 'Married filing jointly or Qualifying surviving spouse' },
  { value: 'head_of_household', label: 'Head of household' },
];

const TAX_CLASSIFICATIONS = [
  'Individual/sole proprietor',
  'C Corporation',
  'S Corporation',
  'Partnership',
  'Trust/estate',
  'LLC',
  'Other',
];

const formatTIN = (raw, type) => {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  if (type === 'EIN') {
    return digits.length <= 2 ? digits : `${digits.slice(0, 2)}-${digits.slice(2)}`;
  }
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

// Company Driver -> Form W-4 (Employee's Withholding Certificate, W-2
// employee). Owner Operator -> Form W-9 (Request for Taxpayer ID,
// 1099 independent contractor). Name/address/SSN-or-EIN-adjacent
// fields already collected in Step 2 aren't re-asked here.
export default function StepTaxForms({ form, set, errors, driverType }) {
  if (driverType === 'Owner Operator') {
    return <W9Fields form={form} set={set} errors={errors} />;
  }
  return <W4Fields form={form} set={set} errors={errors} />;
}

function W4Fields({ form, set, errors }) {
  const step3Total = (Number(form.w4_dependents_qualifying || 0) * 2200)
    + (Number(form.w4_dependents_other || 0) * 500)
    + Number(form.w4_other_credits || 0);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Form W-4 (2026)</h2>
        <p className="text-sm text-muted-foreground mt-1">Employee's Withholding Certificate -- required so we can withhold the correct federal income tax from your pay.</p>
      </div>

      <div>
        <label className="text-xs text-muted-foreground block mb-1">Social Security Number *</label>
        <Input
          value={form.w4_ssn || ''}
          onChange={e => set('w4_ssn', formatTIN(e.target.value, 'SSN'))}
          placeholder="000-00-0000"
          inputMode="numeric"
          className="text-foreground max-w-[200px]"
        />
        {errors.w4_ssn && <p className="text-xs text-red-400 mt-1">{errors.w4_ssn}</p>}
      </div>

      <div>
        <p className="text-xs text-muted-foreground mb-1.5">Filing Status {!form.w4_exempt && '*'}</p>
        <div className="space-y-1.5">
          {FILING_STATUSES.map(fs => (
            <label key={fs.value} className={cn("flex items-center gap-2 text-sm cursor-pointer", form.w4_exempt && "opacity-40 pointer-events-none")}>
              <input
                type="radio"
                name="w4_filing_status"
                checked={form.w4_filing_status === fs.value}
                onChange={() => set('w4_filing_status', fs.value)}
                disabled={form.w4_exempt}
                className="accent-primary"
              />
              <span className="text-foreground">{fs.label}</span>
            </label>
          ))}
        </div>
        {errors.w4_filing_status && <p className="text-xs text-red-400 mt-1">{errors.w4_filing_status}</p>}
      </div>

      <label className={cn("flex items-start gap-2.5 p-3 rounded-xl border border-border cursor-pointer", form.w4_exempt && "border-primary/40 bg-primary/5")}>
        <input type="checkbox" checked={!!form.w4_exempt} onChange={e => set('w4_exempt', e.target.checked)} className="accent-primary mt-0.5" />
        <span className="text-xs text-foreground leading-relaxed">
          <span className="font-medium">I claim exemption from withholding for 2026</span> -- I had no federal income tax liability last year and expect none this year. (Skips the sections below.)
        </span>
      </label>

      {!form.w4_exempt && (
        <>
          <div className="space-y-2">
            <p className="text-sm font-semibold text-foreground">Multiple Jobs or Spouse Works</p>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!form.w4_multiple_jobs} onChange={e => set('w4_multiple_jobs', e.target.checked)} className="accent-primary" />
              <span className="text-foreground">There are only two jobs total (mine and/or my spouse's)</span>
            </label>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-semibold text-foreground">Claim Dependents and Other Credits</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Qualifying children under 17</label>
                <Input type="number" min="0" value={form.w4_dependents_qualifying || ''} onChange={e => set('w4_dependents_qualifying', e.target.value)} className="text-foreground" placeholder="0" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Other dependents</label>
                <Input type="number" min="0" value={form.w4_dependents_other || ''} onChange={e => set('w4_dependents_other', e.target.value)} className="text-foreground" placeholder="0" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Other credits ($)</label>
                <Input type="number" min="0" step="0.01" value={form.w4_other_credits || ''} onChange={e => set('w4_other_credits', e.target.value)} className="text-foreground" placeholder="0.00" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Total: <span className="text-foreground font-medium">${step3Total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></p>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-semibold text-foreground">Other Adjustments (optional)</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Other income ($)</label>
                <Input type="number" min="0" step="0.01" value={form.w4_other_income || ''} onChange={e => set('w4_other_income', e.target.value)} className="text-foreground" placeholder="0.00" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Deductions ($)</label>
                <Input type="number" min="0" step="0.01" value={form.w4_deductions || ''} onChange={e => set('w4_deductions', e.target.value)} className="text-foreground" placeholder="0.00" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Extra withholding / pay period ($)</label>
                <Input type="number" min="0" step="0.01" value={form.w4_extra_withholding || ''} onChange={e => set('w4_extra_withholding', e.target.value)} className="text-foreground" placeholder="0.00" />
              </div>
            </div>
          </div>
        </>
      )}

      <div className="space-y-2">
        <p className="text-sm font-semibold text-foreground">Sign Here</p>
        <p className="text-xs text-muted-foreground">Under penalties of perjury, I declare that this certificate, to the best of my knowledge and belief, is true, correct, and complete.</p>
        <SignaturePad value={form.w4_signature} onChange={v => set('w4_signature', v)} />
        {errors.w4_signature && <p className="text-xs text-red-400 mt-1">{errors.w4_signature}</p>}
      </div>
    </div>
  );
}

function W9Fields({ form, set, errors }) {
  const tinType = form.w9_tin_type || 'SSN';
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Form W-9</h2>
        <p className="text-sm text-muted-foreground mt-1">Request for Taxpayer Identification Number and Certification -- required so we can issue your 1099 as an independent contractor.</p>
      </div>

      <div>
        <label className="text-xs text-muted-foreground block mb-1">Business name (if different from your name, optional)</label>
        <Input value={form.w9_business_name || ''} onChange={e => set('w9_business_name', e.target.value)} className="text-foreground" placeholder={form.company_name || ''} />
      </div>

      <div>
        <p className="text-xs text-muted-foreground mb-1.5">Federal Tax Classification *</p>
        <select
          value={form.w9_tax_classification || ''}
          onChange={e => set('w9_tax_classification', e.target.value)}
          className="w-full h-10 px-3 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none"
        >
          <option value="" className="bg-background">—</option>
          {TAX_CLASSIFICATIONS.map(c => <option key={c} value={c} className="bg-background">{c}</option>)}
        </select>
        {errors.w9_tax_classification && <p className="text-xs text-red-400 mt-1">{errors.w9_tax_classification}</p>}
        {form.w9_tax_classification === 'LLC' && (
          <Input
            value={form.w9_llc_classification || ''}
            onChange={e => set('w9_llc_classification', e.target.value)}
            className="text-foreground mt-2"
            placeholder="Tax classification (C = C corp, S = S corp, P = Partnership)"
          />
        )}
      </div>

      <div>
        <p className="text-xs text-muted-foreground mb-1.5">Taxpayer Identification Number *</p>
        <div className="flex items-center gap-4 mb-2">
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="radio" name="w9_tin_type" checked={tinType === 'SSN'} onChange={() => set('w9_tin_type', 'SSN')} className="accent-primary" />
            <span className="text-foreground">SSN</span>
          </label>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="radio" name="w9_tin_type" checked={tinType === 'EIN'} onChange={() => set('w9_tin_type', 'EIN')} className="accent-primary" />
            <span className="text-foreground">EIN</span>
          </label>
        </div>
        <Input
          value={form.w9_tin || ''}
          onChange={e => set('w9_tin', formatTIN(e.target.value, tinType))}
          placeholder={tinType === 'EIN' ? '00-0000000' : '000-00-0000'}
          inputMode="numeric"
          className="text-foreground max-w-[200px]"
        />
        {errors.w9_tin && <p className="text-xs text-red-400 mt-1">{errors.w9_tin}</p>}
      </div>

      <div className="space-y-2">
        <p className="text-sm font-semibold text-foreground">Certification</p>
        <p className="text-xs text-muted-foreground">Under penalties of perjury, I certify that the number shown on this form is my correct taxpayer identification number, and I am not subject to backup withholding.</p>
        <SignaturePad value={form.w9_signature} onChange={v => set('w9_signature', v)} />
        {errors.w9_signature && <p className="text-xs text-red-400 mt-1">{errors.w9_signature}</p>}
      </div>
    </div>
  );
}
